import { newId } from '@hestia/core';
import { ApiError, Offline, req, surelyOffline } from '../net.js';
import type { Conflict, Op, QueuedOp } from './ops.js';
import { dequeue, pending } from './store.js';

/** Draining the queue back to the server.
 *
 *  Ops go out strictly in the order they were made and strictly one at a time. A
 *  create followed by a tick on the same task only makes sense in that order, and
 *  the queue is short by nature — this is a household list, not a sync engine. */

export interface SyncState {
  /** Best guess at reachability. Set by what actually happened to the last
   *  request, not by `navigator.onLine`, which lies on captive portals. */
  online: boolean;
  /** Writes still waiting to go out. */
  waiting: number;
  syncing: boolean;
  /** Writes the server refused because the world had moved on. */
  conflicts: Conflict[];
}

type Listener = (s: SyncState) => void;

let state: SyncState = { online: !surelyOffline(), waiting: 0, syncing: false, conflicts: [] };
const listeners = new Set<Listener>();

function set(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l(state);
}

export const syncState = () => state;

export function onSync(fn: Listener): () => void {
  listeners.add(fn);
  fn(state);
  return () => { listeners.delete(fn); };
}

/** What just happened to a real request. `navigator.onLine` is not enough on its
 *  own: a phone attached to a wifi network that goes nowhere reports itself
 *  online and fires no `offline` event, so the only trustworthy evidence is
 *  whether the last thing we sent arrived. */
export function noteReachable(ok: boolean) {
  if (state.online !== ok) set({ online: ok });
}

export function dismissConflict(id: string) {
  set({ conflicts: state.conflicts.filter((c) => c.id !== id) });
}

export async function refreshWaiting() {
  set({ waiting: (await pending()).length });
}

/** Everything the drainer needs from the app: a way to say the queue moved (so
 *  views refetch) and a way to say the session is gone. */
let onDrained: (() => void) | null = null;
let onUnauthenticated: (() => void) | null = null;
export function configureSync(hooks: { onDrained?: () => void; onUnauthenticated?: () => void }) {
  onDrained = hooks.onDrained ?? null;
  onUnauthenticated = hooks.onUnauthenticated ?? null;
}

function send(q: QueuedOp): Promise<unknown> {
  const { op } = q;
  switch (op.kind) {
    case 'create':
      return req('POST', '/api/tasks', op.input);
    case 'update':
      return req('PATCH', `/api/tasks/${op.taskId}`, {
        ...op.patch,
        // A base of 0 means the device has never seen a server version of this
        // task — it was created here and its own create is still in the queue
        // ahead of this edit. There is nothing to be stale against, so the guard
        // is left off; with it, the row the create had just written would look
        // newer and the edit would be refused as a conflict with itself.
        ...(op.baseUpdatedAt > 0 ? { ifUnmodifiedSince: op.baseUpdatedAt } : null),
      });
    case 'delete':
      return req('DELETE', `/api/tasks/${op.taskId}`);
    case 'complete':
      return req('POST', `/api/tasks/${op.taskId}/complete`, {
        date: op.date, done: op.done, ifStatus: op.baseStatus,
      });
    case 'move':
      return req('POST', `/api/tasks/${op.taskId}/move`, {
        date: op.date, to: op.to, ifStatus: op.baseStatus,
      });
  }
}

/** A write that will never succeed, however many times it is sent. 409 is the
 *  server saying someone got there first; 404 means the task is gone; 400 means
 *  the payload is not valid and re-sending it cannot change that. Dropping is the
 *  only honest option — the alternative is a queue that never empties. */
const permanent = (err: ApiError) => err.status === 409 || err.status === 404 || err.status === 400;

/** A conflict on something that has since been deleted is not news. Telling
 *  someone their tick did not land on a task that no longer exists is noise. */
const worthReporting = (q: QueuedOp, err: ApiError) =>
  !(err.status === 404 && q.op.kind === 'delete');

let running: Promise<void> | null = null;

export function drain(): Promise<void> {
  if (running) return running;
  // The reset lives in one `finally` around everything. It used to sit around the
  // send loop alone, so the very first call — at boot, with an empty queue —
  // returned early, left the guard set, and every later drain handed back that
  // same finished promise without sending anything.
  running = (async () => {
    let sent = 0;
    const conflicts: Conflict[] = [];
    try {
      const queue = await pending();
      set({ waiting: queue.length });
      if (!queue.length) return;
      if (surelyOffline()) { set({ online: false }); return; }

      set({ syncing: true });
      for (const q of queue) {
        try {
          await send(q);
          await dequeue(q.seq);
          sent++;
          set({ online: true, waiting: state.waiting - 1 });
        } catch (err) {
          if (err instanceof Offline) { set({ online: false }); return; }
          if (err instanceof ApiError && err.status === 401) {
            // The session expired while we were away. Leave the queue alone —
            // signing back in as the same person should still send these.
            onUnauthenticated?.();
            return;
          }
          if (err instanceof ApiError && permanent(err)) {
            await dequeue(q.seq);
            set({ waiting: state.waiting - 1 });
            if (worthReporting(q, err)) {
              conflicts.push({ id: newId(), label: q.label, kind: q.op.kind, at: Date.now() });
            }
            continue;
          }
          // Anything else — a 500, a proxy hiccup — is the server having a bad
          // moment. Stop here and keep the order; the next attempt starts again
          // from this same op.
          return;
        }
      }
    } finally {
      running = null;
      set({ syncing: false, conflicts: [...state.conflicts, ...conflicts] });
      if (sent || conflicts.length) onDrained?.();
    }
  })();
  return running;
}

/** Try the queue whenever there is a reason to think it might go through: the
 *  browser noticing a connection, the app coming back to the foreground, or a
 *  slow drip while it sits open on a flaky one. */
export function watchConnection(): () => void {
  const go = () => { void drain(); };
  const back = () => { if (document.visibilityState === 'visible') go(); };
  window.addEventListener('online', go);
  window.addEventListener('offline', () => set({ online: false }));
  document.addEventListener('visibilitychange', back);
  const timer = window.setInterval(() => { if (state.waiting) go(); }, 60_000);
  return () => {
    window.removeEventListener('online', go);
    document.removeEventListener('visibilitychange', back);
    window.clearInterval(timer);
  };
}
